import { clsx } from 'clsx';
import { useState } from 'react';
import type { PlayerDetail, PlayerListItem } from '#shared/schemas/player';
import type { RatingCard } from '#shared/schemas/rating';
import { fill } from '@/copy';
import { tierFill } from '@/lib/rating';
import { initials, TONE_HEX, toneFor } from '@/ui';
import { copy } from './copy';

/**
 * The player's face in a small circle (24 to 56 px): the `faceUrl` of the active portrait, never
 * the full figure `thumbUrl`, on a tier-colored fill with an ink ring. With no face it shows the
 * initials (Greek works). A player with no rating yet gets a pastel picked from the name, a rated
 * one the tier fill, an unrated one the pale lilac. FROZEN props.
 */
export interface PlayerAvatarProps {
  player: Pick<PlayerListItem | PlayerDetail, 'displayName' | 'portrait'>;
  rating?: Pick<RatingCard, 'tier'> | null;
  size: 24 | 32 | 40 | 56;
}

const BOX: Record<PlayerAvatarProps['size'], { cls: string; font: string; ring: number; shadow: string | undefined }> =
  {
    24: { cls: 'size-6', font: 'text-[10px]', ring: 2, shadow: undefined },
    32: { cls: 'size-8', font: 'text-xs', ring: 2, shadow: undefined },
    40: { cls: 'size-10', font: 'text-sm', ring: 2, shadow: '2px 2px 0 var(--color-shade, #813f98)' },
    56: { cls: 'size-14', font: 'text-lg', ring: 3, shadow: '3px 3px 0 var(--color-shade, #813f98)' },
  };

/** Fill behind the face or the initials. */
export function avatarFill(name: string, rating: PlayerAvatarProps['rating']): string {
  if (rating === undefined) return TONE_HEX[toneFor(name)];
  return tierFill(rating?.tier ?? null);
}

export function PlayerAvatar({ player, rating, size }: PlayerAvatarProps) {
  const src = player.portrait?.faceUrl ?? null;
  // Remember which src failed, so a new src gets a fresh try without an effect.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImg = Boolean(src) && failedSrc !== src;
  const box = BOX[size];
  return (
    <span
      role="img"
      aria-label={fill(copy.label.avatar, { name: player.displayName })}
      data-testid="player-avatar"
      data-size={size}
      className={clsx(
        'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full border-ink font-display font-black italic leading-none text-ink',
        box.cls,
        box.font,
      )}
      style={{
        background: avatarFill(player.displayName, rating),
        borderWidth: box.ring,
        borderStyle: 'solid',
        boxShadow: box.shadow,
      }}
    >
      {showImg ? (
        <img
          src={src ?? undefined}
          alt=""
          className="size-full object-cover"
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <span aria-hidden="true">{initials(player.displayName)}</span>
      )}
    </span>
  );
}
